import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import './modal-shell.less'
import { Modal, ModalContent } from '@mytours/components'

export interface ModalShellProps {
	isOpen: boolean
	className?: string
	backdropDismiss?: boolean
	showBackdrop?: boolean
	children?: React.ReactNode
	onDidPresent?: () => void
	onWillDismiss?: () => void
	onDidDismiss?: () => void
	'data-cy'?: string
}

// Each modal takes the next stacking level when it is created, so one mounted later sits above.
let lastZIndex = 20000
const nextZIndex = () => ++lastZIndex

// The library's iOS modal, mounted in <ion-app>; a visitor's dismissal holds until `isOpen` next turns true.
const ModalShell: React.FC<ModalShellProps> = ({
	isOpen,
	className,
	backdropDismiss = true,
	showBackdrop = true,
	children,
	onDidPresent,
	onWillDismiss,
	onDidDismiss,
	'data-cy': dataCy,
}) => {
	const [zIndex] = useState(nextZIndex)
	const [dismissed, setDismissed] = useState(false)
	// <ion-app> is not in the document yet on the first render; the portal waits for it.
	const [container, setContainer] = useState<HTMLElement | null>(null)
	useLayoutEffect(() => setContainer(document.querySelector<HTMLElement>('ion-app') ?? document.body), [])
	const open = isOpen && !dismissed
	const wasOpen = useRef(open)

	useEffect(() => {
		if (!isOpen) setDismissed(false)
	}, [isOpen])

	useEffect(() => {
		if (wasOpen.current && !open) onWillDismiss?.()
		wasOpen.current = open
	}, [open, onWillDismiss])

	return (
		<Modal
			variant="ios"
			open={open}
			disablePointerDismissal={!backdropDismiss}
			onOpenChange={next => {
				if (!next && backdropDismiss) setDismissed(true)
			}}
			onOpenChangeComplete={presented => (presented ? onDidPresent?.() : onDidDismiss?.())}
		>
			<ModalContent
				container={container}
				transparentBackdrop={!showBackdrop}
				hostProps={{
					className: classNames(className, { 'backdrop-no-dismiss': !backdropDismiss }),
					id: `modal-${zIndex}`,
					style: { zIndex },
					'data-cy': dataCy,
				}}
			>
				<div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					{children}
				</div>
			</ModalContent>
		</Modal>
	)
}

export default ModalShell
